import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ExcursionEntry from '../../components/ExcursionEntry';
import { formatPortDate, formatTimeHM } from '../../utils/dateTimeUtils';
import 'moment/locale/en-gb';
import _union from 'lodash/union';
import Checkbox from '../../components/Checkbox';
import RadioButton from '../../components/RadioButton';
import PlannerContainer from "../../components/PlannerContainer";
import { getFromHub } from "../../actions/networkActions";
import BackLink from "../../components/BackLink";
import ThemableSvgIcon from "../../components/theme/ThemableSvgIcon";
import { getExcursionsText, getExcursionText } from "../../utils/themeUtils";
import { Trans, useLingui } from '@lingui/react/macro';
import { getTranslation } from '../../utils/translationUtils';
import { t } from "@lingui/core/macro";
import capitalize from 'lodash/capitalize';
import { useParams } from 'react-router';


const ExcursionsList = (props) => {
    const {itineraryId} = useParams();
    const itinerary = useSelector(state => state.itinerary.find(itinerary => itinerary.id === itineraryId));
    const [excursions, setExcursions] = useState([]);

    const {t} = useLingui();

    const excursionText = getExcursionText();

    useEffect(() => {
        getFromHub(`prebooking/cruises/itinerary/${itineraryId}/excursions`, (response) => {
            setExcursions(parseExcursionsData(response));
        });
    }, [itineraryId]);

    function parseExcursionsData(response) {
        response.sort(customExcursionSort);

        return response.map(responseEntry => {
            responseEntry.excursion.excursionTypes = responseEntry.excursion.categories.map(category => getTranslation(category.categoryName));
            responseEntry.excursion.currentAvailability = responseEntry.currentAvailability;
            responseEntry.excursion.itinerary = responseEntry.itinerary.id;
            responseEntry.excursion.fromTime = responseEntry.fromTime;
            responseEntry.excursion.toTime = responseEntry.toTime;
            responseEntry.excursion.waitlistActivated = responseEntry.waitlistActivated;
            responseEntry.excursion.currentWaitlistAvailability = responseEntry.currentWaitlistAvailability;
            responseEntry.excursion.duration = responseEntry.duration ? responseEntry.duration : responseEntry.excursion.duration;
            return responseEntry.excursion;
        });
    }

    if (!itinerary) {
        return null;
    }

    return (
        <div>
            <PlannerContainer />
            {/*basic itinerary info*/}
            <div className="container mx-auto my-4 text-primary-dark">
                <BackLink to={t({id: "/itinerary"})} text={t({id: "back_to_excursions_list"})} />
                <Trans id="excursions_list_header">
                    <h1>
                        {excursions.length} {" "}
                        {excursions.length === 1 ? getExcursionText(true) : getExcursionsText(true)} from {" "}
                        {getTranslation(itinerary.port.name).toUpperCase()}
                    </h1>
                    <div className="text-2xl">
                        DAY {itinerary.dayOfCruise} ({formatPortDate(itinerary.date)})
                    </div>
                    <div className="text-3xl">
                        Time in port: {formatTimeHM(itinerary.arrival, "Previous day")} - {formatTimeHM(itinerary.departure, "Next day")}
                    </div>
                </Trans>
            </div>
            {/*filters and filtered excursions*/}
            {excursions.length > 0 && (
                <FilteredExcursions 
                    excursions={excursions} 
                    filterNames={{
                        excursionType: capitalize(t({id:"excursion_type", message: `${excursionText} type`})),
                        duration: t({id: "duration"}),
                        excursionDuration: capitalize(t({id:"excursion_duration", message: `${excursionText} duration`}))
                    }} 
                />
            )}
        </div>
    );
};

class FilteredExcursions extends React.Component {
    constructor(props) {
        super(props);

        const excursionTypes = _union(...this.props.excursions.map(excursion => excursion.excursionTypes)).sort();
        let typeFilterOptions = {};
        for (const excursionType of excursionTypes) {
            typeFilterOptions[excursionType] = {selected: false, available: true};
        }

        this.state = {
            openFilter: "",
            filteredExcursions: this.props.excursions,
            typeFilter: {
                options: typeFilterOptions,
            },
            durationFilter: {
                options: [
                    {
                        label: t({id: "all"}),
                        minDuration: 0,
                        maxDuration: maximumExcursionDuration,
                        disabled: false,
                    },
                    {
                        label: t({id: "2h or less"}),
                        minDuration: 0,
                        maxDuration: 120,
                        disabled: false,
                    },
                    {
                        label: t({id: "2h to 5h"}),
                        minDuration: 120,
                        maxDuration: 300,
                        disabled: false,
                    },
                    {
                        label: t({id: "5h or more"}),
                        minDuration: 300,
                        maxDuration: maximumExcursionDuration,
                        disabled: false,
                    }
                ],
                selectedOption: t({id: "all"}),
                minDuration: 0,
                maxDuration: maximumExcursionDuration
            },
        };
    }

    componentDidMount() {
        this.filterExcursions();
    }


    toggleFilterVisibility = (filterName) => {
        if(filterName === this.state.openFilter) {
            this.setState({
                openFilter: ""
            });
        } else {
            this.setState({
                openFilter: filterName
            })
        }
    };

    filterExcursions = () => {
        const filteredByType = this.props.excursions.filter((excursion) => {
            return this.matchesTypeCriteria(excursion);
        });

        const filteredExcursions = this.props.excursions.filter((excursion) => {
            return this.excursionMatchesFilterCriteria(excursion);
        });

        //check which type filters should be disabled
        //this could be optimized if performance becomes a problem
        let typeFilterOptions = this.state.typeFilter.options;
        for (const type in typeFilterOptions) {
            if(!typeFilterOptions[type].selected) {
                const optionDisabled = filteredExcursions.filter((excursion) => excursion.excursionTypes.indexOf(type) >= 0).length === 0;
                typeFilterOptions[type].disabled = optionDisabled;
            }
        }

        //check which duration filters should be disabled
        //this could be optimized if performance becomes a problem
        this.state.durationFilter.options.map((durationOption) => {
            durationOption.disabled = !filteredByType.some((filteredExcursion) => filteredExcursion.duration <= durationOption.maxDuration && filteredExcursion.duration >= durationOption.minDuration)
            return durationOption;
        });

        this.setState({
            filteredExcursions: filteredExcursions,
            typeFilter: {
                ...this.state.typeFilter,
                options: typeFilterOptions,
            },
        });
    };

    excursionMatchesFilterCriteria(excursion) {
        return this.matchesTypeCriteria(excursion) && this.matchesDurationCriteria(excursion);
    }

    matchesTypeCriteria(excursion) {
        let showExcursion = true;

        for (const type in this.state.typeFilter.options) {
            if(this.state.typeFilter.options[type].selected && excursion.excursionTypes.indexOf(type) < 0) {
                showExcursion = false;
                break;
            }
        }

        return showExcursion;
    }

    matchesDurationCriteria(excursion) {
        //check if excursion duration is between minDuration and maxDuration
        if(excursion.duration > this.state.durationFilter.maxDuration ||
            excursion.duration < this.state.durationFilter.minDuration) {
            return false;
        }

        return true;
    }

    toggleTypeFilterOption = (type) => {
        let typeFilterOptions = this.state.typeFilter.options;
        typeFilterOptions[type].selected = !typeFilterOptions[type].selected;
        this.setState({
            typeFilter: {
                ...this.state.typeFilter,
                options: typeFilterOptions,
            },
        }, this.filterExcursions);

    };

    handleDurationFilterChange = (selectedOption) => {
        this.setState({
            durationFilter: {
                ...this.state.durationFilter,
                selectedOption: selectedOption.label,
                minDuration: selectedOption.minDuration,
                maxDuration: selectedOption.maxDuration
            },
        }, this.filterExcursions);
    };

    clearAllFilters = () => {
        let typeFilterOptions = this.state.typeFilter.options;
        for(const type in typeFilterOptions) {
            typeFilterOptions[type].selected = false;
        }

        this.setState({
            typeFilter: {
                ...this.state.typeFilter,
                options: typeFilterOptions,
            },
            durationFilter: {
                ...this.state.durationFilter,
                selectedOption: "all",
                minDuration: 0,
                maxDuration: maximumExcursionDuration
            },
        }, this.filterExcursions)
    };

    render() {
        const maxDuration = this.props.excursions.reduce((a, b) => Math.max(a,b.duration), 0);
        const minDuration = this.props.excursions.reduce((a, b) => Math.min(a,b.duration), maxDuration);

        return (
            <div>
                {/*filter headers*/}
                <div className="excursion-filters-container">
                    <div className="container mx-auto flex">


                        <FilterHeader
                            filterId="type"
                            filterName={this.props.filterNames.excursionType}
                            openFilter={this.state.openFilter}
                            toggleVisibility={this.toggleFilterVisibility}
                        />

                        <FilterHeader
                            filterId="duration"
                            filterName={this.props.filterNames.duration}
                            openFilter={this.state.openFilter}
                            toggleVisibility={this.toggleFilterVisibility}
                        />
                    </div>
                </div>

                {/*filter content*/}
                <div>
                    {/*type filter*/}
                    {
                        this.state.openFilter === "type" &&
                            <ExcursionFilter
                                filterBy={this.props.filterNames.excursionType}
                                closeFilter={() => this.toggleFilterVisibility("type")}
                                clearFilter={this.clearAllFilters}
                            >
                                <ExcursionTypeFilter
                                    excursionTypes={this.state.typeFilter.options}
                                    handleCheckboxChange={this.toggleTypeFilterOption}
                                />
                            </ExcursionFilter>
                    }
                    {/*duration filter*/}
                    {
                        this.state.openFilter === "duration" &&
                            <ExcursionFilter
                                filterBy={this.props.filterNames.excursionDuration}
                                closeFilter={() => this.toggleFilterVisibility("duration")}
                                clearFilter={this.clearAllFilters}
                            >
                                <ExcursionDurationFilter
                                    minDuration={minDuration}
                                    maxDuration={maxDuration}
                                    options={this.state.durationFilter.options}
                                    handleRadioChange={this.handleDurationFilterChange}
                                    selectedRadio={this.state.durationFilter.selectedOption}
                                />
                            </ExcursionFilter>
                    }
                </div>

                {/*excursions*/}
                <div className="bg-primary-lightest">
                    <div className="container mx-auto py-8">
                        {this.state.filteredExcursions.map((excursion, index)  =>
                            <ExcursionEntry
                                key={excursion.id}
                                first={(index === 0)}
                                {...excursion} />
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const ExcursionFilter = ({ filterBy, closeFilter, clearFilter, children }) => (
    <div>
        {/*filter header*/}
        <div className="container mx-auto">
            <div className=" py-4 border-b">
                <Trans id="filter_by">Filter by</Trans> <span className="font-bold">{filterBy}</span>
                <div
                    className="float-right text-primary-light cursor-pointer"
                    onClick={closeFilter}
                >
                    <ThemableSvgIcon name="x" className="w-4 h-4 inline" />
                </div>
            </div>
        </div>
        {/*filter content*/}
        <div className="container mx-auto">
            <div className="px-4">{children}</div>
        </div>
        {/*filter footer*/}
        <div className="bg-primary-lightest">
            <div className="container mx-auto py-4 text-right">
                <div className="uppercase cursor-pointer" onClick={clearFilter}>
                    <Trans id="clear">clear</Trans>
                </div>
            </div>
        </div>
    </div>
);

const ExcursionTypeFilter = ({ excursionTypes, handleCheckboxChange }) => (
    <div>
        <ul className="list-reset flex flex-wrap">
            {Object.keys(excursionTypes).map((type) => (
                <li className="my-2 h-6 w-full sm:w-1/3" key={type}>
                    <Checkbox
                        label={type}
                        checked={excursionTypes[type].selected}
                        disabled={excursionTypes[type].disabled}
                        handleCheckboxChange={() => handleCheckboxChange(type)}
                        key={type}
                    />
                </li>
            ))}
        </ul>
    </div>
);

const ExcursionDurationFilter = ({ minDuration, maxDuration, options, handleRadioChange, selectedRadio }) => (
    <div>
        <ul className="list-reset sm:flex sm:justify-around">
            {options.map((option, index) => (
                <li className="my-2 h-6" key={index}>
                    <RadioButton
                        label={option.label}
                        _onChange={() => handleRadioChange(option)}
                        minval={option.minDuration}
                        maxval={option.maxDuration}
                        isSelected={selectedRadio === option.label}
                        isDisabled={option.disabled}
                    />
                </li>
            ))}
        </ul>
    </div>
);

const FilterHeader = ({ filterId, filterName, openFilter, toggleVisibility }) => {
    const filterOpen = openFilter === filterId;
    return (
        <div
            className={
                "pt-4 flex-1 text-center uppercase border-l-2 border-r border-white cursor-pointer select-none" +
                (filterOpen ? " text-primary bg-white" : " text-white")
            }
            onClick={() => toggleVisibility(filterId)}
        >
            <div className="mb-4">{filterName}</div>
            <div className="font-bold">{filterOpen ? " \uFE3F " : "\uFE40"}</div>
        </div>
    );
};

export default ExcursionsList;

const maximumExcursionDuration = 10080; // 7 days

function customExcursionSort(a, b) {
    // take booking code up until the first space
    const aBookingCode = a.excursion.bookingCode.split(" ")[0];
    const bBookingCode = b.excursion.bookingCode.split(" ")[0];

    // if booking codes are the same, sort by fromTime
    if (aBookingCode === bBookingCode) {
        return a.fromTime < b.fromTime ? -1 : 1;
    }

    //otherwise sort by booking codes
    return aBookingCode > bBookingCode ? 1 : -1;
}
